import React, { type FunctionComponent, type PropsWithChildren } from 'react'
import { ThemeProvider, type IPP_SUPPORTED_LOCALE } from '@retailer-platform/shared-components'
import { type TDS_LOCALES } from '@retailer-platform/shared-components/src/tds'
import { TourProvider } from '@reactour/tour'
import { NavProvider } from '../../nav/utils/NavProvider'
import { UserFeedbackProvider } from '../../user-feedback/UserFeedbackProvider'
import { AdminControlsProvider } from '../../../../utils/contexts/admin-controls/AdminControlsContext'
import { BetaEnvBanner } from '../../beta-env-banner/BetaEnvBanner'
import { App as LegacyAppProvider } from '../../../../legacy/dash-components/app/App'
import { AppRouter } from '../../../../routes/AppRouter'
import { getDefaultTourProviderProps } from '../../../../utils/tour/tour.utils'
import { AppProviderStyling } from './AppProviderStyling'

interface Props {
  locale: IPP_SUPPORTED_LOCALE
}

const tourProviderProps = getDefaultTourProviderProps()

// `children` is the content slot: legacy renders its default (<AppRouter />), while the
// TanStack shell passes the native tree in — so the app chrome (theme, nav, feedback,
// tour, banner) mounts identically under both routers and only the routed leaf differs.
export const AppUIProviders: FunctionComponent<PropsWithChildren<Props>> = ({
  locale,
  children,
}) => (
  <ThemeProvider locale={locale as unknown as TDS_LOCALES} enableIdsRouting>
    <NavProvider>
      <UserFeedbackProvider>
        <AdminControlsProvider>
          <TourProvider {...tourProviderProps}>
            <BetaEnvBanner />
            <AppProviderStyling>
              <LegacyAppProvider>{children ?? <AppRouter />}</LegacyAppProvider>
            </AppProviderStyling>
          </TourProvider>
        </AdminControlsProvider>
      </UserFeedbackProvider>
    </NavProvider>
  </ThemeProvider>
)

export default AppUIProviders
